import { CommonModule } from '@angular/common';
import { Component, computed, effect, EventEmitter, forwardRef, Input, Output, Signal, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '@vendasta/galaxy/filter/chips';
import { switchMap, map } from 'rxjs/operators';
import { Market } from './interface';
import { MarketFilterService } from './market-filter.service';

function marketFilterFn(market: Market, searchTerm: string): boolean {
  searchTerm = searchTerm?.toLowerCase();
  const includesMarketId = market.market_id.toLowerCase().includes(searchTerm);
  const includesMarketName = market.name.toLowerCase().includes(searchTerm);
  return includesMarketId || includesMarketName;
}

@Component({
  template: `
    <glxy-form-field bottomSpacing="none" *ngIf="availableOptions() as availableOptions">
      <input
        [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
        type="text"
        matInput
        [formControl]="formControl"
        [matAutocomplete]="auto"
        data-testid="filter-market-input"
      />
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn(availableOptions)">
        <mat-option *ngFor="let option of filteredOptions()" [value]="option">
          {{ option?.name }}
        </mat-option>
      </mat-autocomplete>
    </glxy-form-field>
  `,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
  ],
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => MarketFilterInputComponent),
    },
  ],
  standalone: true,
})
export class MarketFilterInputComponent implements GalaxyAbstractFilterInput {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }
  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<Market | string> = new FormControl<Market | string>('', {
    nonNullable: true,
  });

  restrictedGroupId = toSignal(this.marketFilterService.getCurrentGroupId());
  restrictedMarket = toSignal(this.marketFilterService.getCurrentMarket());
  searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });

  _fieldId = signal('');
  availableOptions$ = toObservable(this.searchTerm).pipe(
    takeUntilDestroyed(),
    switchMap((searchTerm) => {
      const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.market_id;
      return this.marketFilterService.getAllMarkets().pipe(
        map((markets) => {
          if (!filterTerm) {
            return markets;
          }
          return markets.filter((market) => marketFilterFn(market, filterTerm));
        }),
      );
    }),
  );

  availableOptions = toSignal(this.availableOptions$);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length > 0 && value[0].string) {
      this.formControl.setValue(value[0].string);
    }
  }

  clearValue(): void {
    this.formControl.setValue('');
  }

  filteredOptions: Signal<Market[]> = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.market_id;
    return availableOptions.filter((market) => marketFilterFn(market, filterTerm ?? ''));
  });

  constructor(private readonly marketFilterService: MarketFilterService) {
    effect(
      () => {
        const value = this.searchTerm();
        const restrictedGroupId = this.restrictedGroupId();
        if (restrictedGroupId) {
          this.formControl.disable();
        }

        if (value === '') {
          this.valueChanges.emit([]);
          return;
        }
        if (typeof value === 'string') {
          this.valueChanges.emit([{ string: value }]);
          return;
        }
        this.valueChanges.emit([{ string: value?.market_id }]);
      },
      { allowSignalWrites: true },
    );
  }

  displayFn(availableOptions: Market[]): (market: Market | string) => string {
    return (market: Market | string): string => {
      if (!market || !availableOptions) {
        return '';
      }
      if (typeof market === 'string') {
        const foundMarket = availableOptions.find((o) => o.market_id === market);
        return foundMarket?.name || market;
      }
      return market?.name || '';
    };
  }
}
